import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { PushPin, PushPinOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import Wordmark from '../common/Wordmark';
import { NAVY, motion } from './shellTokens';
import { isModifiedClick } from './navConfig';

interface RailBrandProps {
  /** Rail is showing labels (pinned, hovered or focused). */
  expanded: boolean;
  pinned: boolean;
  onTogglePin: () => void;
  reducedMotion: boolean;
  /** Mobile drawer has no pin affordance — it is a modal, not a rail. */
  showPin?: boolean;
}

/**
 * Rail header: the Stratify glyph, the product name, the attribution line and
 * the pin control.
 *
 * The glyph is always rendered at the same size and the same x-offset in both
 * states, so expanding the rail slides the TEXT in beside a mark that never
 * moves. Cross-fading the mark itself reads as a flicker at 180ms.
 */
const RailBrand: React.FC<RailBrandProps> = ({
  expanded,
  pinned,
  onTogglePin,
  reducedMotion,
  showPin = true,
}) => (
  <Box
    sx={{
      position: 'relative',
      // Same vertical rhythm as the AppBar (64px) plus breathing room, so the
      // first nav item lines up with the first row of page content.
      px: 1.75,
      pt: 2.25,
      pb: 1.75,
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      minHeight: 76,
    }}
  >
    <Box
      component={RouterLink}
      to="/dashboard"
      onClick={(e: React.MouseEvent) => {
        // Real <a href>: a modified click is the browser's to handle.
        if (isModifiedClick(e)) return;
      }}
      aria-label="Stratify ERP — go to dashboard"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        flexGrow: 1,
        textDecoration: 'none',
        borderRadius: 2,
        p: 0.25,
        '&:focus-visible': {
          outline: `2px solid ${NAVY.focus}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* 40px tile, horizontally centred inside the 76px rail when collapsed
          (76 - 2*14 padding - 40 = 8, hence the ml nudge below). */}
      <Wordmark size={34} tone="dark" showText={false} />

      <Box
        sx={{
          minWidth: 0,
          // Collapsed: zero-width and clipped rather than unmounted, so the
          // label does not pop in a frame after the width finishes animating.
          flex: expanded ? '1 1 auto' : '0 0 0px',
          opacity: expanded ? 1 : 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          transition: motion(reducedMotion, 'opacity .16s ease .04s'),
        }}
      >
        <Typography
          sx={{
            color: NAVY.text,
            fontSize: '0.9375rem',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}
        >
          Stratify ERP
        </Typography>
        <Typography
          sx={{
            color: NAVY.textFaint,
            fontSize: '0.625rem',
            lineHeight: 1.4,
            letterSpacing: '0.01em',
          }}
        >
          Powered by Fensso Tech LLP
        </Typography>
      </Box>
    </Box>

    {showPin && (
      <Tooltip title={pinned ? 'Unpin sidebar (auto-collapse)' : 'Pin sidebar open'} placement="right">
        {/* Kept mounted but visually collapsed, so tab order is stable and the
            control never appears to jump into existence mid-animation. */}
        <IconButton
          size="small"
          onClick={onTogglePin}
          aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
          aria-pressed={pinned}
          sx={{
            flexShrink: 0,
            color: pinned ? NAVY.accent : NAVY.textMuted,
            width: expanded ? 32 : 0,
            p: expanded ? 0.75 : 0,
            opacity: expanded ? 1 : 0,
            overflow: 'hidden',
            pointerEvents: expanded ? 'auto' : 'none',
            transition: motion(reducedMotion, 'opacity .16s ease, width .16s ease'),
            '&:hover': { backgroundColor: NAVY.hover, color: NAVY.text },
            '&:focus-visible': { outline: `2px solid ${NAVY.focus}`, outlineOffset: 1 },
          }}
        >
          {pinned ? <PushPin sx={{ fontSize: 17 }} /> : <PushPinOutlined sx={{ fontSize: 17 }} />}
        </IconButton>
      </Tooltip>
    )}

  </Box>
);

export default RailBrand;
